<template>

  <!-- Empty State Message -->
  <div v-if="!reportTests || reportTests.length === 0" class="bg-white px-6 py-4 my-4 border border-gray-200 rounded-lg">
    <div class="text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No samples assigned to farms</h3>
      <p class="text-sm text-gray-500 mb-4">
        Reports can be generated here once samples have been assigned to parcels. 
      </p>
      <p class="text-xs text-gray-400">
        To assign samples, ensure the farms are mapped and use the Assignment tools on the Records tab.
      </p>
    </div>
  </div>

  <!-- Reports Section -->
  <div v-if="reportTests && reportTests.length > 0" class="mb-6">
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <div class="mb-3">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          Reports
        </h3>
      </div>
      
      <div class="bg-white rounded-lg border border-gray-300">
        <table class="table-auto w-full">
          <thead>
            <tr class="bg-gray-50 text-gray-700 font-bold text-sm">
              <th scope="col" class="px-3 py-3 text-left">Report Type</th>
              <th scope="col" class="px-3 py-3 text-left">Status</th>
              <th scope="col" class="px-3 py-3 text-left">Email Status</th>
              <th v-if="isDairygoldTask()" scope="col" class="px-3 py-3 text-left">Sent to Lab</th>
              <th scope="col" class="px-3 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr class="bg-white hover:bg-gray-50">
              <td class="px-3 py-3 text-gray-900 font-medium">
                All Farms Report
              </td>
              <td class="px-3 py-3 text-gray-700">
                <div class="space-y-1" v-if="!isLoadingReports">
                  <!-- Combined Task Report Status -->
                  <div class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Task:</span>
                    <span :class="getStatusClass(reportTask.combine_task_report_status)">
                      <svg v-if="shouldShowIcon(reportTask.combine_task_report_status)" class="w-3 h-3 mr-1" :class="{'animate-spin': reportTask.combine_task_report_status === 'generating'}" fill="currentColor" viewBox="0 0 20 20">
                        <path :d="getStatusIcon(reportTask.combine_task_report_status)"></path>
                      </svg>
                      {{ getStatusTextWithProgress(reportTask.combine_task_report_status, reportTask.combine_task_report_progress) }}
                    </span>
                  </div>
                  <!-- Combined Soil Analysis Status -->
                  <div class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Soil:</span>
                    <span :class="getStatusClass(reportTask.combine_soil_analysis_status)">
                      <svg v-if="shouldShowIcon(reportTask.combine_soil_analysis_status)" class="w-3 h-3 mr-1" :class="{'animate-spin': reportTask.combine_soil_analysis_status === 'generating'}" fill="currentColor" viewBox="0 0 20 20">
                        <path :d="getStatusIcon(reportTask.combine_soil_analysis_status)"></path>
                      </svg>
                      {{ getStatusTextWithProgress(reportTask.combine_soil_analysis_status, reportTask.combine_soil_analysis_progress) }}
                    </span>
                  </div>
                </div>
                <div v-else class="inline-flex rounded-md shadow-sm" role="group">
                  <div class="inline-flex items-center px-2 py-0 text-[10px] font-medium text-gray-400 bg-gray-200 rounded-lg animate-pulse">
                    Loading...
                  </div>
                </div>
              </td>
              <td class="px-3 py-3 text-gray-700">
                <div class="space-y-1">
                  <div v-if="reportTask.combine_task_report" class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Task:</span>
                    <span v-if="reportTask.combine_task_report_emailed_at" class="flex items-center text-green-600">
                      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      {{ formatDate(reportTask.combine_task_report_emailed_at) }}
                    </span>
                    <span v-else class="text-gray-400">Not sent</span>
                  </div>
                  <div v-if="reportTask.combine_soil_analysis_report" class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Soil:</span>
                    <span v-if="reportTask.combine_soil_analysis_emailed_at" class="flex items-center text-green-600">
                      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      {{ formatDate(reportTask.combine_soil_analysis_emailed_at) }}
                    </span>
                    <span v-else class="text-gray-400">Not sent</span>
                  </div>
                </div>
              </td>
              <td v-if="isDairygoldTask()" class="px-3 py-3 text-gray-700">
                <div class="space-y-1">
                  <div v-if="reportTask.combine_task_report" class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Task:</span>
                    <span v-if="reportTask.combine_task_report_ftp_uploaded_at" class="flex items-center text-green-600">
                      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      {{ formatDate(reportTask.combine_task_report_ftp_uploaded_at) }}
                    </span>
                    <span v-else class="text-gray-400">Not sent</span>
                  </div>
                  <div v-if="reportTask.combine_soil_analysis_report" class="flex items-center text-xs">
                    <span class="w-8 text-gray-600">Soil:</span>
                    <span v-if="reportTask.combine_soil_analysis_ftp_uploaded_at" class="flex items-center text-green-600">
                      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      {{ formatDate(reportTask.combine_soil_analysis_ftp_uploaded_at) }}
                    </span>
                    <span v-else class="text-gray-400">Not sent</span>
                  </div>
                </div>
              </td>
              <td class="px-3 py-3">
                <TakCombineReportsMenuItem
                  v-if="permissionsCheck()"
                  @report-generation-started="handleCombinedReportGenerationStarted"
                  :DownloadTaskReportCombine="
                    route('app.task.download-combine', {
                      task: reportTask.id,
                      report_type: 'task_report',
                    })
                  "
                  :createTaskReportCombine="
                    route('app.task.create-combine', {
                      task: reportTask.id,
                      report_type: 'task_report',
                      recreate: reportTask.combine_task_report ? true : false,
                    })
                  "
                  :TaskReportCombineStatus="reportTask.combine_task_report"
                  :DownloadAnalysisReportCombine="
                    route('app.task.download-combine', {
                      task: reportTask.id,
                      report_type: 'soil_analysis',
                    })
                  "
                  :CreateAnalysisReportCombine="
                    route('app.task.create-combine', {
                      task: reportTask.id,
                      report_type: 'soil_analysis',
                      recreate: reportTask.combine_soil_analysis_report
                        ? true
                        : false,
                    })
                  "
                  :AnalysisReportCombineStatus="
                    reportTask.combine_soil_analysis_report
                  "
                  :HasNotStartedTest="reportTask.task_has_not_started_test"
                  :HasTestsWithNoLabResult="
                    reportTask.task_has_tests_without_lab_result
                  "
                  :HasNoTestsWithLabResult="
                    reportTask.task_has_no_tests_with_lab_result
                  "
                  :HasUnmatchedSample="reportTask.task_has_unmatched_samples"
                  :canEmail="permissionsCheck()"
                  :taskId="reportTask.id"
                  :combineTaskReportEmailedAt="reportTask.combine_task_report_emailed_at"
                  :combineSoilAnalysisEmailedAt="reportTask.combine_soil_analysis_emailed_at"
                  :canUploadToDairygold="canUploadToDairygold()"
                  :isDairygoldTask="isDairygoldTask()"
                  :availableLabCertificates="availableLabCertificates || []"
                  @ftp-upload-success="refreshReportData"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Individual Reports Section (Collapsible) -->
  <div v-if="reportTests && reportTests.length > 0" class="border border-gray-200 rounded-lg">
    <div 
      class="bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
      @click="showIndividualReports = !showIndividualReports"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <svg 
            class="w-4 h-4 mr-2 text-gray-600 transition-transform"
            :class="showIndividualReports ? 'transform rotate-90' : ''"
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
          </svg>
          <h4 class="font-medium text-gray-800">Individual Farm Reports</h4>
        </div>
      </div>
    </div>
    
    <div v-if="showIndividualReports" class="p-4">
      <div class="mb-2">

      </div>
      
      <table class="table-auto min-w-full divide-y divide-gray-300 border border-gray-300 border-collapse rounded-lg">
        <thead>
          <tr class="bg-gray-100 font-bold text-sm text-gray-700">
            <th scope="col" class="px-3 py-2.5 text-left">Test</th>
            <th scope="col" class="px-3 py-2.5 text-left">Farm</th>
            <th scope="col" class="px-3 py-2.5 text-left">Report Generated</th>
            <th scope="col" class="px-3 py-2.5 text-left">Email Status</th>
            <th v-if="isDairygoldTask()" scope="col" class="px-3 py-2.5 text-left">Sent to Lab</th>
            <th scope="col" class="px-3 py-2.5 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(test, index) in reportTests"
            :key="index"
            class="odd:bg-white even:bg-gray-50 text-gray-900 text-sm hover:bg-gray-100"
          >
            <td class="px-3 py-2.5">
              {{ test.id }}
            </td>
            <td class="px-3 py-2.5 cursor-pointer">
              <span v-on:click="selectFarm(test.farmer_id, test.farm_name)">
                {{ test.farm_name }}
              </span>
            </td>
            <td class="px-3 py-2.5">
              <div class="space-y-1" v-if="test.loading !== true">
                <!-- Task Report Status -->
                <div class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Task:</span>
                  <span :class="getStatusClass(test.task_report_status_text)">
                    <svg v-if="shouldShowIcon(test.task_report_status_text)" class="w-3 h-3 mr-1" :class="{'animate-spin': test.task_report_status_text === 'generating'}" fill="currentColor" viewBox="0 0 20 20">
                      <path :d="getStatusIcon(test.task_report_status_text)"></path>
                    </svg>
                    {{ getStatusTextWithProgress(test.task_report_status_text, test.task_report_progress) }}
                  </span>
                </div>
                <!-- Soil Analysis Status -->
                <div class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Soil:</span>
                  <span :class="getStatusClass(test.soil_analysis_status_text)">
                    <svg v-if="shouldShowIcon(test.soil_analysis_status_text)" class="w-3 h-3 mr-1" :class="{'animate-spin': test.soil_analysis_status_text === 'generating'}" fill="currentColor" viewBox="0 0 20 20">
                      <path :d="getStatusIcon(test.soil_analysis_status_text)"></path>
                    </svg>
                    {{ getStatusTextWithProgress(test.soil_analysis_status_text, test.soil_analysis_progress) }}
                  </span>
                </div>
              </div>
              <div v-else class="inline-flex rounded-md shadow-sm" role="group">
                <div class="inline-flex items-center px-2 py-0 text-[10px] font-medium text-gray-400 bg-gray-200 rounded-lg animate-pulse">
                  Loading...
                </div>
              </div>
            </td>
            <td class="px-3 py-2.5">
              <div class="space-y-1">
                <div v-if="test.task_report_status" class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Task:</span>
                  <span v-if="test.task_report_emailed_at" class="flex items-center text-green-600">
                    <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ formatDate(test.task_report_emailed_at) }}
                  </span>
                  <span v-else class="text-gray-400">Not sent</span>
                </div>
                <div v-if="test.soil_analysis_status" class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Soil:</span>
                  <span v-if="test.soil_analysis_emailed_at" class="flex items-center text-green-600">
                    <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ formatDate(test.soil_analysis_emailed_at) }}
                  </span>
                  <span v-else class="text-gray-400">Not sent</span>
                </div>
              </div>
            </td>
            <td v-if="isDairygoldTask()" class="px-3 py-2.5">
              <div class="space-y-1">
                <div v-if="test.task_report_status" class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Task:</span>
                  <span v-if="test.task_report_ftp_uploaded_at" class="flex items-center text-green-600">
                    <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ formatDate(test.task_report_ftp_uploaded_at) }}
                  </span>
                  <span v-else class="text-gray-400">Not sent</span>
                </div>
                <div v-if="test.soil_analysis_status" class="flex items-center text-xs">
                  <span class="w-8 text-gray-600">Soil:</span>
                  <span v-if="test.soil_analysis_ftp_uploaded_at" class="flex items-center text-green-600">
                    <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ formatDate(test.soil_analysis_ftp_uploaded_at) }}
                  </span>
                  <span v-else class="text-gray-400">Not sent</span>
                </div>
              </div>
            </td>
            <td class="px-3 py-2.5">
              <div>
                <!-- Individual farm reports check - add organization_id validation -->
                <test-reports-menu-item
                  v-if="
                    test.farm_hashid &&
                    ($page.props.auth.user.role == 'admin' ||
                      ($page.props.auth.user.role == 'consultant' &&
                        ($page.props.auth.user.organisation.type == 'lab' ||
                          $page.props.auth.user.organisation_id == task.organisation_id)) ||
                      ($page.props.auth.user.role == 'consultant_user' &&
                        ($page.props.auth.user.organisation.type == 'lab' ||
                          $page.props.auth.user.organisation_id == task.organisation_id)) ||
                      ($page.props.auth.user.role == 'contractor' &&
                        $page.props.auth.user.organisation_id ==
                          task.assigned_to_organisation) ||
                      ($page.props.auth.user.role == 'soil_sampler' &&
                        task.assigned_to == $page.props.auth.user.id) ||
                      $page.props.auth.user.role == 'farm_owner' ||
                      $page.props.auth.user.id == task.task_creator)
                  "
                  @report-generation-started="handleIndividualReportGenerationStarted"
                  :createLink="
                    $page.props.auth.user.role !== 'farm_owner' ? 
                    route('app.task.create-pdf', {
                      task: test.task_hashid,
                      farm: test?.farm_hashid,
                      recreate: test.soil_analysis_status,
                      report_type: 'soil_analysis',
                    }) : null
                  "
                  :pdfCreated="test.soil_analysis_status"
                  :warning="{
                    hasNotStartedTest: task.task_has_not_started_test,
                    hasTestsWithNoLabResult:
                      task.task_has_tests_without_lab_result,
                    hasUnmatchedSample: task.task_has_unmatched_samples,
                  }"
                  :createTaskReport="
                    $page.props.auth.user.role !== 'farm_owner' ?
                    route('app.task.create-pdf', {
                      task: test.task_hashid,
                      farm: test?.farm_hashid,
                      recreate: test.task_report_status,
                      report_type: 'task_report',
                    }) : null
                  "
                  :viewTaskReport="
                    route('app.task.view-pdf', {
                      task: test.task_hashid,
                      farm: test.farm_hashid,
                      report_type: 'task_report',
                    })
                  "
                  :viewSoilAnalysisReport="
                    ['admin', 'farm_owner'].includes($page.props.auth.user.role) ||
                    ($page.props.auth.user.role == 'consultant' &&
                      ($page.props.auth.user.organisation.type == 'lab' ||
                        $page.props.auth.user.organisation_id == task.organisation_id)) ||
                    ($page.props.auth.user.role == 'consultant_user' &&
                      ($page.props.auth.user.organisation.type == 'lab' ||
                        $page.props.auth.user.organisation_id == task.organisation_id))
                  "
                  :viewLink="
                    route('app.task.view-pdf', {
                      task: test.task_hashid,
                      farm: test.farm_hashid,
                    })
                  "
                  :taskReportCreated="test.task_report_status"
                  :canEmail="permissionsCheck()"
                  :taskId="test.task_hashid"
                  :farmId="test.farm_hashid"
                  :taskReportEmailedAt="test.task_report_emailed_at"
                  :soilAnalysisEmailedAt="test.soil_analysis_emailed_at"
                  :emailReportRoute="route('app.task.email-report', { 
                    task: test.task_hashid, 
                    farm: test.farm_hashid 
                  })"
                  :canUploadToDairygold="canUploadToDairygold()"
                  :isDairygoldTask="isDairygoldTask()"
                  :availableLabCertificates="availableLabCertificates || []"
                  @ftp-upload-success="refreshReportData"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div v-if="tests.length === 0" class="bg-white px-6 py-4 my-4 border border-gray-200 rounded-lg">
    <div class="text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No samples assigned to farms</h3>
      <p class="text-sm text-gray-500 mb-4">
        Reports can be generated here once samples have been assigned to parcels.  
      </p>
      <p class="text-xs text-gray-400">
        To assign samples, ensure the farms are mapped and use the Assignment tools on the Records tab.
      </p>
    </div>
  </div>
</template>

<script>
import TestReportsMenuItem from "@/Components/TestReportsMenuItem";
import TakCombineReportsMenuItem from "@/Components/TaskCombineReportsMenuItem";
import { useToast } from "vue-toastification";
import axios from "axios";
export default {
  components: {
    TestReportsMenuItem,
    TakCombineReportsMenuItem,
  },

  props: {
    tests: Array,
    task: Object,
    availableLabCertificates: Array,
  },
  data() {
    return {
      toast: null,
      reportTests: [],
      reportTask: {},
      interval: null,
      isLoadingReports: false,
      hasLoadedReports: false,
      showIndividualReports: false, // Collapsed by default to emphasize combined reports
    };
  },
  mounted() {
    this.toast = useToast();

    this.reportTests = this.tests;
    this.reportTask = this.task;

    // Load initial fast data first
    this.loadReportsData();

    // Checking every 10,000 milliseconds if there is an update to the report
    this.interval = setInterval(() => {
      this.loadReportsData();
    }, 10000);
  },
  beforeUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
  methods: {
    permissionsCheck() {
      const user = this.$page.props.auth.user;
      const userOrgType = user.organisation?.type;
      
      return (
        user.role == "admin" ||
        (user.role == "consultant" &&
          (userOrgType == "lab" ||
            user.organisation_id == this.task.organisation_id)) ||
        (user.role == "consultant_user" &&
          (userOrgType == "lab" ||
            user.organisation_id == this.task.organisation_id)) ||
        (user.role == "contractor" &&
          user.organisation_id == this.task.assigned_to_organisation) ||
        (user.role == "soil_sampler" &&
          this.task.assigned_to == user.id) ||
        user.id == this.task.task_creator
      );
    },

    getStatusTextWithProgress(status, progress = null) {
      switch (status) {
        case 'not_created':
          return 'Not generated';
        case 'generating':
          return progress !== null && progress !== undefined 
            ? `Generating... ${progress}%` 
            : 'Generating...';
        case 'generated':
          return 'Generated';
        case 'failed':
          return 'Failed';
        default:
          return 'Not generated';
      }
    },

    // Keep old method for backward compatibility
    getStatusText(status) {
      return this.getStatusTextWithProgress(status, null);
    },

    getStatusClass(status) {
      switch (status) {
        case 'not_created':
          return 'text-gray-500';
        case 'generating':
          return 'text-blue-600 flex items-center';
        case 'generated':
          return 'text-green-600 flex items-center';
        case 'failed':
          return 'text-red-600 flex items-center';
        default:
          return 'text-gray-500';
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'generating':
          return 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z';
        case 'generated':
          return 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z';
        case 'failed':
          return 'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z';
        default:
          return null;
      }
    },

    shouldShowIcon(status) {
      return ['generating', 'generated', 'failed'].includes(status);
    },

    canUploadToDairygold() {
      const user = this.$page.props.auth.user;
      
      // Allow Livefarm Admins (admin role)
      if (user.role === 'admin') {
        return true;
      }
      
      // Allow Dairygold organisation users (consultant or consultant_user roles)
      if (user.organisation && 
          user.organisation.name === 'Dairygold' && 
          (user.role === 'consultant' || user.role === 'consultant_user')) {
        return true;
      }
      
      return false;
    },

    isDairygoldTask() {
      return this.task.lab === 'Dairygold';
    },

    selectFarm(index, farmer) {
      this.$root.search.farmer.selected = index;
      this.$root.search.farmer.search = farmer;

      let vm = this;

      axios.post(route("app.farmer.map.view", index)).then(function (response) {
        vm.$root.farmer = response.data;
      });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    loadReportsData() {
      // Stage 1: Load basic farm info quickly (without file checks)
      if (!this.hasLoadedReports) {
        this.isLoadingReports = true;
        axios
          .get(route("app.task.reports.status.fast", this.task.id))
          .then((response) => {
            this.reportTests = response.data.tests;
            this.reportTask = response.data.task;
            this.hasLoadedReports = true;
            this.isLoadingReports = false;
            
            // Stage 2: Load complete data with file checks
            setTimeout(() => {
              this.loadCompleteReportsData();
            }, 100);
          })
          .catch((error) => {
            console.error('Failed to load fast report data:', error);
            this.isLoadingReports = false;
            // Fallback to complete data load
            this.loadCompleteReportsData();
          });
      } else {
        // If already loaded basic data, just refresh with complete data
        this.loadCompleteReportsData();
      }
    },

    loadCompleteReportsData() {
      axios
        .get(route("app.task.reports.status", this.task.id))
        .then((response) => {
          // Update with complete data including file checks
          this.reportTests = response.data.tests;
          this.reportTask = response.data.task;
        })
        .catch((error) => {
          console.error('Failed to load complete report data:', error);
        });
    },

    refreshReportData() {
      // Refresh the report data immediately to show updated FTP upload timestamps
      this.loadCompleteReportsData();
    },

    // Immediate status update methods for instant user feedback
    setIndividualReportGenerating(farmHashId, reportType) {
      const testIndex = this.reportTests.findIndex(test => test.farm_hashid === farmHashId);
      if (testIndex >= 0) {
        if (reportType === 'task_report') {
          this.reportTests[testIndex].task_report_status_text = 'generating';
        } else if (reportType === 'soil_analysis') {
          this.reportTests[testIndex].soil_analysis_status_text = 'generating';
        }
        this.$forceUpdate(); // Ensure Vue detects the change
      }
    },

    setCombinedReportGenerating(reportType) {
      if (reportType === 'task_report') {
        this.reportTask.combine_task_report_status = 'generating';
      } else if (reportType === 'soil_analysis') {
        this.reportTask.combine_soil_analysis_status = 'generating';
      }
      this.$forceUpdate(); // Ensure Vue detects the change
    },

    setAllIndividualReportsGenerating(reportType) {
      // Update all individual farm reports to generating status immediately
      this.reportTests.forEach((test, index) => {
        if (reportType === 'task_report') {
          this.reportTests[index].task_report_status_text = 'generating';
        } else if (reportType === 'soil_analysis') {
          this.reportTests[index].soil_analysis_status_text = 'generating';
        }
      });
      this.$forceUpdate(); // Ensure Vue detects the change
    },

    // Event handlers for when report generation starts
    handleCombinedReportGenerationStarted(reportType) {
      this.setCombinedReportGenerating(reportType);
      
      // Also immediately update all individual farm reports to generating
      this.setAllIndividualReportsGenerating(reportType);
      
      // Refresh data after a short delay to get backend confirmation
      setTimeout(() => {
        this.loadCompleteReportsData();
      }, 2000);
    },

    handleIndividualReportGenerationStarted(data) {
      this.setIndividualReportGenerating(data.farmId, data.reportType);
      // Refresh data after a short delay to get backend confirmation
      setTimeout(() => {
        this.loadCompleteReportsData();
      }, 2000);
    },
  },
};
</script>